/**
 * @file
 * @fileOverview Enhanced A-Frame component for creating a cinematic camera effect with advanced rendering options and performance controls.
 * @version 3.0.0
 * @date 2025-02-27
 */

import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { TAARenderPass } from 'three/examples/jsm/postprocessing/TAARenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { BokehPass } from 'three/examples/jsm/postprocessing/BokehPass.js';
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js';
import { CopyShader } from 'three/examples/jsm/shaders/CopyShader.js';
import { SSAOPass } from 'three/examples/jsm/postprocessing/SSAOPass.js';
import { SSAARenderPass } from 'three/examples/jsm/postprocessing/SSAARenderPass.js';

// Custom Shader Definitions
const FilmGrainShader = {
  uniforms: {
    "tDiffuse": { value: null },
    "amount": { value: 0.05 },
    "speed": { value: 1.0 },
    "time": { value: 0 }
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float amount;
    uniform float speed;
    uniform float time;
    varying vec2 vUv;

    // Luminance coefficient from Rec. 709
    const vec3 lumCoeff = vec3(0.2126, 0.7152, 0.0722);
    
    // Define luminance function explicitly
    float luminance(vec3 color) {
      return dot(color, lumCoeff);
    }

    // High quality pseudorandom noise function
    float rand(vec2 co) {
      return fract(sin(dot(co.xy, vec2(12.9898, 78.233))) * 43758.5453);
    }

    void main() {
      vec4 color = texture2D(tDiffuse, vUv);
      
      // Generate time-based noise
      vec2 noiseCoord = vUv * vec2(1.0, 1.0) * speed + vec2(time * 0.1, time * 0.1);
      float noise = rand(noiseCoord) - 0.5;
      
      // Apply noise with more impact on mid-tones and less on shadows/highlights
      float lum = luminance(color.rgb);
      float noiseMask = 1.0 - 2.0 * abs(lum - 0.5);
      
      color.rgb += noise * amount * (0.5 + 0.5 * noiseMask);
      
      gl_FragColor = color;
    }
  `
};

const ChromaticAberrationShader = {
  uniforms: {
    "tDiffuse": { value: null },
    "amount": { value: 0.005 },
    "radialFactor": { value: 0.5 }
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float amount;
    uniform float radialFactor;
    varying vec2 vUv;
    
    // Luminance coefficient from Rec. 709
    const vec3 lumCoeff = vec3(0.2126, 0.7152, 0.0722);
    
    // Define luminance function explicitly
    float luminance(vec3 color) {
      return dot(color, lumCoeff);
    }
    
    void main() {
      vec2 center = vec2(0.5, 0.5);
      vec2 uv = vUv;
      
      // Calculate distance from center for radial effect
      float dist = distance(uv, center) * 2.0;
      
      // Mix between uniform and radial distribution
      float aberrationAmount = amount * mix(1.0, dist, radialFactor);
      
      // Sample each color channel with offset
      vec4 colorR = texture2D(tDiffuse, uv + vec2(aberrationAmount, 0.0));
      vec4 colorG = texture2D(tDiffuse, uv);
      vec4 colorB = texture2D(tDiffuse, uv - vec2(aberrationAmount, 0.0));
      
      gl_FragColor = vec4(colorR.r, colorG.g, colorB.b, colorG.a);
    }
  `
};

const MotionBlurShader = {
  uniforms: {
    "tDiffuse": { value: null },
    "tPrevious": { value: null },
    "strength": { value: 0.5 }
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform sampler2D tPrevious;
    uniform float strength;
    varying vec2 vUv;
    
    // Luminance coefficient from Rec. 709
    const vec3 lumCoeff = vec3(0.2126, 0.7152, 0.0722);
    
    // Define luminance function explicitly
    float luminance(vec3 color) {
      return dot(color, lumCoeff);
    }
    
    void main() {
      vec4 currentColor = texture2D(tDiffuse, vUv);
      vec4 previousColor = texture2D(tPrevious, vUv);
      
      // Simple blend between current and previous frame
      gl_FragColor = mix(currentColor, previousColor, strength);
    }
  `
};

const LensDistortionShader = {
  uniforms: {
    "tDiffuse": { value: null },
    "strength": { value: 0.15 },
    "cylindricalRatio": { value: 2.0 }
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float strength;
    uniform float cylindricalRatio;
    varying vec2 vUv;
    
    // Luminance coefficient from Rec. 709
    const vec3 lumCoeff = vec3(0.2126, 0.7152, 0.0722);
    
    // Define luminance function explicitly
    float luminance(vec3 color) {
      return dot(color, lumCoeff);
    }
    
    vec2 distort(vec2 uv) {
      // Convert to polar coordinates
      vec2 centered = uv * 2.0 - 1.0;
      float radius = length(centered);
      float factor = pow(radius, 2.0) * strength;
      
      // Mix between spherical and cylindrical distortion
      float x = centered.x * mix(factor, 1.0, (1.0 - cylindricalRatio) * 0.5);
      float y = centered.y * mix(factor, 1.0, (1.0 - cylindricalRatio) * 0.5);
      
      // Convert back to texture coordinates
      return vec2(x, y) * 0.5 + 0.5;
    }
    
    void main() {
      vec2 distortedUV = vUv + (distort(vUv) - vUv);
      
      // Check if we're outside the texture bounds
      if (distortedUV.x < 0.0 || distortedUV.x > 1.0 || 
          distortedUV.y < 0.0 || distortedUV.y > 1.0) {
        gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
      } else {
        gl_FragColor = texture2D(tDiffuse, distortedUV);
      }
    }
  `
};

// Enhanced Contrast Adaptive Sharpening Shader
const EnhancedCASShader = {
  uniforms: {
    "tDiffuse": { value: null },
    "sharpness": { value: 0.5 },
    "denoise": { value: 0.0 },
    "edgeDetection": { value: 0.6 }
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float sharpness;
    uniform float denoise;
    uniform float edgeDetection;
    varying vec2 vUv;
    
    // Edge detection function
    float detectEdge(vec3 center, vec3 top, vec3 right, vec3 bottom, vec3 left) {
      // Roberts cross operator
      float edge = length(center - top) + length(center - right) + 
                   length(center - bottom) + length(center - left);
      return smoothstep(0.0, edgeDetection, edge);
    }
    
    void main() {
      vec2 texSize = vec2(1024.0, 1024.0); // Fallback size
      #ifdef GL_OES_standard_derivatives
        vec2 texSize = vec2(textureSize(tDiffuse, 0));
      #endif
      
      vec2 texelSize = 1.0 / texSize;
      
      // Sample neighboring pixels
      vec3 center = texture2D(tDiffuse, vUv).rgb;
      vec3 top = texture2D(tDiffuse, vUv + texelSize * vec2(0.0, -1.0)).rgb;
      vec3 right = texture2D(tDiffuse, vUv + texelSize * vec2(1.0, 0.0)).rgb;
      vec3 bottom = texture2D(tDiffuse, vUv + texelSize * vec2(0.0, 1.0)).rgb;
      vec3 left = texture2D(tDiffuse, vUv + texelSize * vec2(-1.0, 0.0)).rgb;
      
      vec3 topLeft = texture2D(tDiffuse, vUv + texelSize * vec2(-1.0, -1.0)).rgb;
      vec3 topRight = texture2D(tDiffuse, vUv + texelSize * vec2(1.0, -1.0)).rgb;
      vec3 bottomLeft = texture2D(tDiffuse, vUv + texelSize * vec2(-1.0, 1.0)).rgb;
      vec3 bottomRight = texture2D(tDiffuse, vUv + texelSize * vec2(1.0, 1.0)).rgb;
      
      // Find min and max for clamping
      vec3 minColor = min(min(min(top, right), min(bottom, left)), center);
      vec3 maxColor = max(max(max(top, right), max(bottom, left)), center);
      
      // Calculate local contrast
      vec3 localContrast = (maxColor - minColor) / (maxColor + minColor + vec3(0.001));
      
      // CAS algorithm
      vec3 sharpenedColor = center + (2.0 * center - (top + right + bottom + left) * 0.25) * sharpness;
      
      // Apply local contrast adaptive sharpening
      sharpenedColor = mix(center, sharpenedColor, 1.0 - length(localContrast));
      
      // Clamp to local color range to prevent ringing
      sharpenedColor = clamp(sharpenedColor, minColor, maxColor);
      
      // Apply denoise based on edge detection
      float edge = detectEdge(center, top, right, bottom, left);
      sharpenedColor = mix(center, sharpenedColor, 1.0 - (edge * denoise));
      
      gl_FragColor = vec4(sharpenedColor, 1.0);
    }
  `
};

// Enhanced Depth of Field shader
const EnhancedDOFShader = {
  uniforms: {
    "tDiffuse": { value: null },
    "tDepth": { value: null },
    "tBlurred": { value: null },
    "camera": { value: null },
    "focusDistance": { value: 5.0 },
    "aperture": { value: 0.025 },
    "nearTransition": { value: 1.0 },
    "farTransition": { value: 1.0 },
    "nearBlur": { value: 1.0 },
    "farBlur": { value: 1.0 },
    "maxBlur": { value: 1.0 },
    "bokehShape": { value: 0 }, // 0 = circular, 1 = hexagonal, 2 = octagonal
    "bokehRotation": { value: 0.0 },
    "depthRange": { value: 1.0 }
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform sampler2D tDepth;
    uniform sampler2D tBlurred;
    uniform float focusDistance;
    uniform float aperture;
    uniform float nearTransition;
    uniform float farTransition;
    uniform float nearBlur;
    uniform float farBlur;
    uniform float maxBlur;
    uniform int bokehShape;
    uniform float bokehRotation;
    uniform float depthRange;
    
    varying vec2 vUv;
    
    // Luminance coefficient from Rec. 709
    const vec3 lumCoeff = vec3(0.2126, 0.7152, 0.0722);
    
    // Define luminance function explicitly
    float luminance(vec3 color) {
      return dot(color, lumCoeff);
    }
    
    float readDepth(sampler2D depthSampler, vec2 coord) {
      float depth = texture2D(depthSampler, coord).x;
      return depth * depthRange;
    }
    
    // Bokeh shape function
    float bokehPattern(vec2 coords, vec2 center, float radius, int shape, float rotation) {
      vec2 dir = coords - center;
      float dist = length(dir);
      
      if (shape == 0) {
        // Circular
        return step(dist, radius);
      } else if (shape == 1) {
        // Hexagonal
        const float PI = 3.14159265359;
        float angle = atan(dir.y, dir.x) - rotation;
        float hexAngle = angle - (PI * 2.0 / 6.0) * floor((angle + PI / 6.0) / (PI / 3.0));
        float hexDist = dist / cos(hexAngle);
        return step(hexDist, radius);
      } else {
        // Octagonal
        const float PI = 3.14159265359;
        float angle = atan(dir.y, dir.x) - rotation;
        float octAngle = angle - (PI * 2.0 / 8.0) * floor((angle + PI / 8.0) / (PI / 4.0));
        float octDist = dist / cos(octAngle);
        return step(octDist, radius);
      }
    }
    
    void main() {
      // Get base color
      vec4 color = texture2D(tDiffuse, vUv);
      
      // Use a fixed depth for testing without actual depth texture
      float depth;
      #ifdef USE_DEPTH_TEXTURE
        depth = readDepth(tDepth, vUv);
      #else
        // Simulate depth based on screen position (farther at center)
        vec2 center = vec2(0.5, 0.5);
        float distFromCenter = length(vUv - center);
        depth = mix(focusDistance * 1.5, focusDistance * 0.5, smoothstep(0.0, 0.5, distFromCenter));
      #endif
      
      // Calculate blur amount based on depth
      float nearFactor = smoothstep(focusDistance - nearTransition * aperture, 
                                  focusDistance, depth) * nearBlur;
      float farFactor = smoothstep(focusDistance, 
                                 focusDistance + farTransition * aperture, depth) * farBlur;
      float blurAmount = min((nearFactor + farFactor) * aperture, maxBlur);
      
      // If no blur needed, just return original color
      if (blurAmount < 0.001) {
        gl_FragColor = color;
        return;
      }
      
      // Use pre-blurred texture for better performance
      vec4 blurredColor = texture2D(tBlurred, vUv);
      
      // Apply chromatic aberration to blurred areas for lens effect
      vec2 dir = vUv - vec2(0.5);
      float aberrationAmount = 0.005 * blurAmount;
      vec4 rColor = texture2D(tBlurred, vUv + dir * aberrationAmount);
      vec4 gColor = blurredColor;
      vec4 bColor = texture2D(tBlurred, vUv - dir * aberrationAmount);
      
      blurredColor.r = rColor.r;
      blurredColor.b = bColor.b;
      
      // Final color blend
      gl_FragColor = mix(color, blurredColor, clamp(blurAmount, 0.0, 1.0));
    }
  `
};

// Enhanced vignette shader with corner emphasis
const EnhancedVignetteShader = {
  uniforms: {
    "tDiffuse": { value: null },
    "offset": { value: 0.95 },
    "darkness": { value: 0.7 },
    "vignetteColor": { value: new THREE.Vector3(0.0, 0.0, 0.0) },
    "cornerWeight": { value: 0.3 }
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float offset;
    uniform float darkness;
    uniform vec3 vignetteColor;
    uniform float cornerWeight;
    varying vec2 vUv;
    
    // Luminance coefficient from Rec. 709
    const vec3 lumCoeff = vec3(0.2126, 0.7152, 0.0722);
    
    // Define luminance function explicitly
    float luminance(vec3 color) {
      return dot(color, lumCoeff);
    }
    
    void main() {
      vec4 color = texture2D(tDiffuse, vUv);
      
      // Calculate distance from center
      vec2 center = vec2(0.5, 0.5);
      vec2 coords = vUv - center;
      float dist = length(coords * vec2(1.0, 1.0 - cornerWeight * abs(coords.x)));
      
      // Create vignette effect
      float vignette = smoothstep(offset, offset - 0.15, dist);
      
      // Apply vignette
      color.rgb = mix(color.rgb, vignetteColor, (1.0 - vignette) * darkness);
      
      gl_FragColor = color;
    }
  `
};

// Enhanced color grading with LUT support
const ColorGradingShader = {
  uniforms: {
    "tDiffuse": { value: null },
    "tLUT": { value: null },
    "lutEnabled": { value: false },
    "brightness": { value: 0.0 },
    "contrast": { value: 1.0 },
    "saturation": { value: 1.0 },
    "vibrance": { value: 0.0 },
    "shadows": { value: 0.0 },
    "highlights": { value: 0.0 },
    "shadowsTint": { value: new THREE.Color(0.0, 0.0, 0.2) },
    "highlightsTint": { value: new THREE.Color(1.0, 0.9, 0.8) }
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform sampler2D tLUT;
    uniform bool lutEnabled;
    uniform float brightness;
    uniform float contrast;
    uniform float saturation;
    uniform float vibrance;
    uniform float shadows;
    uniform float highlights;
    uniform vec3 shadowsTint;
    uniform vec3 highlightsTint;
    
    varying vec2 vUv;
    
    // Luminance coefficient from Rec. 709
    const vec3 lumCoeff = vec3(0.2126, 0.7152, 0.0722);
    
    // Define luminance function explicitly
    float luminance(vec3 color) {
      return dot(color, lumCoeff);
    }
    
    // Apply LUT (Look-Up Table)
    vec3 applyLUT(vec3 color, sampler2D lut) {
      const float LUT_SIZE = 16.0;
      const float LUT_LAST_INDEX = 15.0;

      // Scale the color values to the proper lookup range
      color = clamp(color, 0.0, 1.0) * LUT_LAST_INDEX;
      
      float blueIndex = floor(color.b);
      vec2 blueInterp = vec2(blueIndex, blueIndex + 1.0) / LUT_SIZE;
      
      // Base LUT coordinate
      vec2 lutCoord = vec2(color.r, color.g) / LUT_SIZE;
      
      // Sample the LUT
      vec4 lutLow = texture2D(lut, lutCoord + vec2(0.0, blueInterp.x));
      vec4 lutHigh = texture2D(lut, lutCoord + vec2(0.0, blueInterp.y));
      
      // Interpolate between the two blue slices
      return mix(lutLow.rgb, lutHigh.rgb, fract(color.b));
    }
    
    void main() {
      vec4 color = texture2D(tDiffuse, vUv);
      
      // Calculate luminance using our defined function
      float lum = luminance(color.rgb);
      
      // Apply base adjustments
      color.rgb = (color.rgb - 0.5) * contrast + 0.5 + brightness;
      
      // Apply saturation
      vec3 grayscale = vec3(lum);
      color.rgb = mix(grayscale, color.rgb, saturation);
      
      // Apply vibrance (saturation that preserves skin tones)
      float satMask = 1.0 - dot(max(vec3(0.0), color.rgb - 0.6), vec3(0.3333));
      color.rgb = mix(grayscale, color.rgb, 1.0 + (vibrance * satMask));
      
      // Apply shadows and highlights adjustments
      float shadowsMask = 1.0 - smoothstep(0.0, 0.3, lum);
      float highlightsMask = smoothstep(0.7, 1.0, lum);
      
      // Adjust shadows
      color.rgb = mix(color.rgb, color.rgb * shadowsTint, shadowsMask * shadows);
      
      // Adjust highlights
      color.rgb = mix(color.rgb, color.rgb * highlightsTint, highlightsMask * highlights);
      
      // Apply LUT if enabled
      if (lutEnabled) {
        color.rgb = applyLUT(color.rgb, tLUT);
      }
      
      gl_FragColor = color;
    }
  `
};

// NEW SHADERS

// Anamorphic Lens Flare shader - creates horizontal light streaks from bright sources
const AnamorphicFlareShader = {
  uniforms: {
    "tDiffuse": { value: null },
    "intensity": { value: 0.5 },
    "threshold": { value: 0.9 },
    "color": { value: new THREE.Vector3(0.6, 0.8, 1.0) }, // Blueish flare color
    "stretch": { value: 5.0 } // Horizontal stretch factor
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float intensity;
    uniform float threshold;
    uniform vec3 color;
    uniform float stretch;
    varying vec2 vUv;
    
    // Luminance function for brightness detection
    const vec3 lumCoeff = vec3(0.2126, 0.7152, 0.0722);
    float luminance(vec3 color) {
      return dot(color, lumCoeff);
    }
    
    void main() {
      vec4 texel = texture2D(tDiffuse, vUv);
      
      // Extract highlights that exceed threshold
      float luma = luminance(texel.rgb);
      float highlight = max(0.0, luma - threshold) / (1.0 - threshold);
      
      // Apply horizontal blur to create anamorphic streak
      float streakSum = 0.0;
      float weight = 1.0;
      float weightSum = weight;
      
      // Sample in horizontal direction
      for (int i = 1; i <= 8; i++) {
        float offset = float(i) / 8.0 * stretch / 100.0;
        
        // Right sample
        float rightLuma = luminance(texture2D(tDiffuse, vec2(vUv.x + offset, vUv.y)).rgb);
        float rightHighlight = max(0.0, rightLuma - threshold) / (1.0 - threshold);
        weight = 1.0 / (1.0 + float(i));
        streakSum += rightHighlight * weight;
        weightSum += weight;
        
        // Left sample
        float leftLuma = luminance(texture2D(tDiffuse, vec2(vUv.x - offset, vUv.y)).rgb);
        float leftHighlight = max(0.0, leftLuma - threshold) / (1.0 - threshold);
        streakSum += leftHighlight * weight;
        weightSum += weight;
      }
      
      // Normalize and apply color
      float streak = streakSum / weightSum;
      vec3 flare = streak * color * intensity;
      
      // Blend with original image
      gl_FragColor = vec4(texel.rgb + flare, texel.a);
    }
  `
};

// Film Scratches and Dust shader - adds temporal scratches and dust to simulate old film
const FilmDamageShader = {
  uniforms: {
    "tDiffuse": { value: null },
    "time": { value: 0.0 },
    "scratches": { value: 0.3 },
    "dust": { value: 0.2 },
    "dustSize": { value: 1.0 },
    "scratchWidth": { value: 0.5 }
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float time;
    uniform float scratches;
    uniform float dust;
    uniform float dustSize;
    uniform float scratchWidth;
    varying vec2 vUv;
    
    // Luminance coefficient from Rec. 709
    const vec3 lumCoeff = vec3(0.2126, 0.7152, 0.0722);
    
    // Define luminance function explicitly
    float luminance(vec3 color) {
      return dot(color, lumCoeff);
    }
    
    // Noise functions
    float random(vec2 p) {
      return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
    }
    
    float noise(vec2 p) {
      vec2 i = floor(p);
      vec2 f = fract(p);
      f = f * f * (3.0 - 2.0 * f);
      return mix(
        mix(random(i), random(i + vec2(1.0, 0.0)), f.x),
        mix(random(i + vec2(0.0, 1.0)), random(i + vec2(1.0, 1.0)), f.x),
        f.y
      );
    }
    
    void main() {
      vec4 texel = texture2D(tDiffuse, vUv);
      
      // Sample at current time and position for randomness
      float n = random(vUv + time * 0.01);
      
      // Vertical scratches that move horizontally over time
      float scratchPos = floor(vUv.y * 20.0) * 0.1 + time * 0.03;
      float scratch = smoothstep(0.0, scratchWidth, 
                       1.0 - abs(sin(scratchPos * 4.0 + n * 20.0))) * 
                       step(1.0 - scratches, noise(vec2(scratchPos, vUv.y * 10.0)));
                       
      // Dust particles
      float dustNoise = noise(vUv * dustSize * 100.0 + vec2(time));
      float dustMask = step(1.0 - dust * 0.2, dustNoise);
      
      // Apply damage to image
      texel.rgb = mix(texel.rgb, vec3(1.0), scratch * 0.5);
      texel.rgb = mix(texel.rgb, vec3(0.0), dustMask * 0.5);
      
      gl_FragColor = texel;
    }
  `
};

// Radial Blur Shader - creates motion-like blur radiating from center
const RadialBlurShader = {
  uniforms: {
    "tDiffuse": { value: null },
    "strength": { value: 0.3 },
    "centerX": { value: 0.5 },
    "centerY": { value: 0.5 }
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float strength;
    uniform float centerX;
    uniform float centerY;
    varying vec2 vUv;
    
    // Luminance coefficient from Rec. 709
    const vec3 lumCoeff = vec3(0.2126, 0.7152, 0.0722);
    
    // Define luminance function explicitly
    float luminance(vec3 color) {
      return dot(color, lumCoeff);
    }
    
    void main() {
      vec2 center = vec2(centerX, centerY);
      vec2 dir = vUv - center;
      float distance = length(dir);
      
      // Normalize direction
      dir = dir / distance;
      
      // Closer to center = less blur
      float adjustedStrength = strength * distance * 2.0;
      
      // Accumulate samples along the ray
      vec4 color = vec4(0.0);
      float total = 0.0;
      
      // Sample multiple times along the ray
      const int samples = 6; // Keep sample count low for performance
      for (int i = 0; i < samples; i++) {
        float weight = 1.0 - float(i) / float(samples);
        float offset = adjustedStrength * float(i) / float(samples);
        
        // Calculate sample position along the ray
        vec2 sampleUv = vUv - dir * offset;
        
        // Only sample within texture bounds
        if (sampleUv.x >= 0.0 && sampleUv.x <= 1.0 && 
            sampleUv.y >= 0.0 && sampleUv.y <= 1.0) {
          color += texture2D(tDiffuse, sampleUv) * weight;
          total += weight;
        }
      }
      
      // Normalize accumulated color
      gl_FragColor = color / total;
    }
  `
};

// Film Color Emulation Shader - mimics specific film stock characteristics
const FilmEmulationShader = {
    uniforms: {
      "tDiffuse": { value: null },
      "filmType": { value: 0 }, // 0: Normal, 1: Kodak, 2: Fuji, 3: Cross-processed
      "intensity": { value: 1.0 }
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform sampler2D tDiffuse;
      uniform int filmType;
      uniform float intensity;
      varying vec2 vUv;
      
      // Luminance function
      const vec3 lumCoeff = vec3(0.2126, 0.7152, 0.0722);
      float luminance(vec3 color) {
        return dot(color, lumCoeff);
      }
      
      // Film response curves for different film types
      vec3 filmResponse(vec3 color, int type) {
        vec3 result = color;
        float luma = luminance(color);
        
        if (type == 1) {
          // Kodak-like - warm shadows, neutral highlights
          vec3 shadowColor = vec3(1.0, 0.9, 0.7); // Warm yellows in shadows
          vec3 midColor = vec3(1.0, 0.95, 0.9);   // Slightly warm midtones
          vec3 highColor = vec3(0.95, 0.95, 1.0); // Cool highlights
          
          // Apply contrast curve
          result = pow(color, vec3(1.1));
          
          // Adjust color based on luminance
          float shadowAmt = 1.0 - smoothstep(0.0, 0.4, luma);
          float highAmt = smoothstep(0.7, 1.0, luma);
          float midAmt = 1.0 - shadowAmt - highAmt;
          
          // Apply color grading
          result *= mix(vec3(1.0), shadowColor, shadowAmt * 0.5);
          result *= mix(vec3(1.0), midColor, midAmt * 0.3);
          result *= mix(vec3(1.0), highColor, highAmt * 0.3);
        }
        else if (type == 2) {
          // Fuji-like - cool shadows, saturated greens
          vec3 shadowColor = vec3(0.8, 0.9, 1.0);    // Cool shadows
          vec3 midColor = vec3(0.95, 1.05, 0.95);    // Enhanced greens in midtones
          vec3 highColor = vec3(1.0, 0.99, 0.95);    // Slightly warm highlights
          
          // Apply contrast curve
          result = pow(color, vec3(1.05));
          
          // Adjust color based on luminance
          float shadowAmt = 1.0 - smoothstep(0.0, 0.4, luma);
          float highAmt = smoothstep(0.7, 1.0, luma);
          float midAmt = 1.0 - shadowAmt - highAmt;
          
          // Apply color grading
          result *= mix(vec3(1.0), shadowColor, shadowAmt * 0.4);
          result *= mix(vec3(1.0), midColor, midAmt * 0.4);
          result *= mix(vec3(1.0), highColor, highAmt * 0.2);
          
          // Enhance greens
          float greenEnhance = max(0.0, color.g - (color.r + color.b) * 0.4);
          result.g += greenEnhance * 0.2;
        }
        else if (type == 3) {
          // Cross-processed look
          vec3 shadowColor = vec3(0.3, 0.8, 1.0);   // Cyan shadows
          vec3 highColor = vec3(1.2, 1.0, 0.2);     // Yellow-green highlights
          
          // Increase contrast
          result = pow(color, vec3(1.3));
          
          // Apply color shifts
          float shadowAmt = 1.0 - smoothstep(0.0, 0.5, luma);
          float highAmt = smoothstep(0.5, 1.0, luma);
          
          // Apply color grading
          result *= mix(vec3(1.0), shadowColor, shadowAmt * 0.6);
          result *= mix(vec3(1.0), highColor, highAmt * 0.6);
        }
        
        return mix(color, result, intensity);
      }
      
      void main() {
        vec4 texel = texture2D(tDiffuse, vUv);
        
        // Apply film emulation if type is not 0 (normal)
        if (filmType > 0) {
          texel.rgb = filmResponse(texel.rgb, filmType);
        }
        
        gl_FragColor = texel;
      }
    `
  };
  
  // Cinemascope 2.35:1 Aspect Ratio Bars
  const CinemascopeLetterboxShader = {
    uniforms: {
      "tDiffuse": { value: null },
      "aspectRatio": { value: 2.35 }, // Default to 2.35:1 Cinemascope
      "opacity": { value: 1.0 }
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform sampler2D tDiffuse;
      uniform float aspectRatio;
      uniform float opacity;
      varying vec2 vUv;
      
      // Luminance coefficient from Rec. 709
      const vec3 lumCoeff = vec3(0.2126, 0.7152, 0.0722);
      
      // Define luminance function explicitly
      float luminance(vec3 color) {
        return dot(color, lumCoeff);
      }
      
      void main() {
        vec4 texel = texture2D(tDiffuse, vUv);
        
        // Calculate screen aspect ratio
        float screenRatio = 16.0/9.0; // Assuming 16:9 display
        
        // Calculate letterbox height
        float letterboxHeight = (aspectRatio - screenRatio) / (2.0 * aspectRatio);
        
        // Apply letterbox bars
        float yPos = vUv.y;
        if (yPos < letterboxHeight || yPos > (1.0 - letterboxHeight)) {
          texel.rgb = mix(texel.rgb, vec3(0.0), opacity);
        }
        
        gl_FragColor = texel;
      }
    `
  };
  
  // Dynamic LOD system for performance monitoring and quality adjustment
  const PerformanceMonitor = {
    constructor() {
      this.frameTimeSamples = [];
      this.maxSamples = 30;
      this.lastFrameTime = 0;
      this.averageFrameTime = 0;
      this.targetFrameTime = 1000/60; // Target 60fps
      this.qualityAdjustmentNeeded = false;
      this.currentQuality = 'auto';
    },
    
    start() {
      this.lastFrameTime = performance.now();
    },
    
    end() {
      const currentTime = performance.now();
      const frameDuration = currentTime - this.lastFrameTime;
      this.lastFrameTime = currentTime;
      
      this.frameTimeSamples.push(frameDuration);
      if (this.frameTimeSamples.length > this.maxSamples) {
        this.frameTimeSamples.shift();
      }
      
      // Calculate average frame time
      let total = 0;
      for (let i = 0; i < this.frameTimeSamples.length; i++) {
        total += this.frameTimeSamples[i];
      }
      this.averageFrameTime = total / this.frameTimeSamples.length;
      
      // Check if quality adjustment is needed
      if (this.currentQuality === 'auto' && this.frameTimeSamples.length >= this.maxSamples) {
        if (this.averageFrameTime > this.targetFrameTime * 1.2) {
          this.qualityAdjustmentNeeded = 'down';
        } else if (this.averageFrameTime < this.targetFrameTime * 0.7) {
          this.qualityAdjustmentNeeded = 'up';
        } else {
          this.qualityAdjustmentNeeded = false;
        }
      }
    },
    
    getQualityLevel() {
      if (this.currentQuality !== 'auto') {
        return this.currentQuality;
      }
      
      // FPS-based quality estimation
      const fps = 1000 / this.averageFrameTime;
      if (fps < 20) return 'low';
      if (fps < 40) return 'medium';
      if (fps < 55) return 'high';
      return 'ultra';
    },
    
    reset() {
      this.frameTimeSamples = [];
      this.lastFrameTime = 0;
      this.averageFrameTime = 0;
    }
  };
  
  // Main enhanced cinematic camera component
  AFRAME.registerComponent('cinematic-camera-pro', {
    schema: {
      // Performance and quality settings
      quality: {type: 'string', default: 'auto', oneOf: ['low', 'medium', 'high', 'ultra', 'auto']},
      adaptiveQuality: {type: 'boolean', default: true},
      renderScale: {type: 'number', default: 1.0, min: 0.5, max: 4.0},
      targetFPS: {type: 'number', default: 60, min: 30, max: 144},
      
      // Core settings
      active: {type: 'boolean', default: true},
      transitionDuration: {type: 'number', default: 1500},
      
      // Letterbox settings
      letterboxEnabled: {type: 'boolean', default: true},
      letterboxHeight: {type: 'number', default: 0.15, min: 0, max: 0.5},
      
      // Vignette settings
      vignetteEnabled: {type: 'boolean', default: true},
      vignetteStrength: {type: 'number', default: 0.3, min: 0, max: 1},
      vignetteColor: {type: 'color', default: '#000000'},
      vignetteCornerWeight: {type: 'number', default: 0.3, min: 0, max: 1},
      
      // Anti-aliasing settings
      aaMethod: {type: 'string', default: 'fxaa', oneOf: ['none', 'fxaa', 'smaa', 'taa', 'msaa']},
      fxaaLevel: {type: 'number', default: 4, oneOf: [4, 8, 16]},
      msaaSamples: {type: 'number', default: 4, oneOf: [2, 4, 8, 16]},
      
      // Bloom settings
      bloomEnabled: {type: 'boolean', default: true},
      bloomStrength: {type: 'number', default: 0.8, min: 0, max: 3},
      bloomRadius: {type: 'number', default: 0.3, min: 0, max: 1},
      bloomThreshold: {type: 'number', default: 0.9, min: 0, max: 1},
      
      // Depth of field settings
      dofEnabled: {type: 'boolean', default: true},
      dofFocus: {type: 'number', default: 3.0, min: 0.1, max: 100},
      dofAperture: {type: 'number', default: 0.02, min: 0, max: 0.1},
      dofMaxBlur: {type: 'number', default: 0.8, min: 0, max: 3},
      dofNearTransition: {type: 'number', default: 1.0, min: 0.1, max: 10},
      dofFarTransition: {type: 'number', default: 1.0, min: 0.1, max: 10},
      dofNearBlur: {type: 'number', default: 1.0, min: 0, max: 2},
      dofFarBlur: {type: 'number', default: 1.0, min: 0, max: 2},
      dofBokehShape: {type: 'string', default: 'circular', oneOf: ['circular', 'hexagonal', 'octagonal']},
      dofBokehRotation: {type: 'number', default: 0, min: 0, max: 6.28},
      
      // Sharpening settings
      sharpeningEnabled: {type: 'boolean', default: true},
      sharpeningStrength: {type: 'number', default: 0.5, min: 0, max: 1},
      sharpeningDenoise: {type: 'number', default: 0.3, min: 0, max: 1},
      sharpeningEdgeDetection: {type: 'number', default: 0.6, min: 0, max: 1},
      
      // Color grading settings
      colorGradingEnabled: {type: 'boolean', default: true},
      colorBrightness: {type: 'number', default: 0.05, min: -1, max: 1},
      colorContrast: {type: 'number', default: 0.2, min: 0, max: 2},
      colorSaturation: {type: 'number', default: 0.1, min: -1, max: 2},
      colorVibrance: {type: 'number', default: 0.2, min: 0, max: 1},
      colorShadows: {type: 'number', default: 0.1, min: -1, max: 1},
      colorHighlights: {type: 'number', default: 0.1, min: -1, max: 1},
      colorShadowTint: {type: 'color', default: '#000033'},
      colorHighlightTint: {type: 'color', default: '#FFF5E0'},
      lutEnabled: {type: 'boolean', default: false},
      lutTexture: {type: 'string', default: ''},
      
      // Film grain settings
      filmGrainEnabled: {type: 'boolean', default: false},
      filmGrainAmount: {type: 'number', default: 0.05, min: 0, max: 0.5},
      filmGrainSpeed: {type: 'number', default: 1.0, min: 0, max: 5},
      
      // Chromatic aberration settings
      chromaticAberrationEnabled: {type: 'boolean', default: false},
      chromaticAberrationAmount: {type: 'number', default: 0.005, min: 0, max: 0.02},
      chromaticAberrationRadialFactor: {type: 'number', default: 0.5, min: 0, max: 1},
      
      // Lens distortion settings
      lensDistortionEnabled: {type: 'boolean', default: false},
      lensDistortionStrength: {type: 'number', default: 0.15, min: 0, max: 1},
      lensDistortionCylindricalRatio: {type: 'number', default: 2.0, min: 0, max: 4},
      
      // Motion blur settings
      motionBlurEnabled: {type: 'boolean', default: false},
      motionBlurStrength: {type: 'number', default: 0.5, min: 0, max: 1},
      
      // Anamorphic flare settings
      anamorphicFlareEnabled: {type: 'boolean', default: false},
      anamorphicFlareIntensity: {type: 'number', default: 0.5, min: 0, max: 2},
      anamorphicFlareThreshold: {type: 'number', default: 0.85, min: 0, max: 1},
      anamorphicFlareColor: {type: 'color', default: '#99CCFF'},
      anamorphicFlareStretch: {type: 'number', default: 5.0, min: 1, max: 10},
      
      // Film damage settings
      filmDamageEnabled: {type: 'boolean', default: false},
      filmDamageScratchAmount: {type: 'number', default: 0.3, min: 0, max: 1},
      filmDamageDustAmount: {type: 'number', default: 0.2, min: 0, max: 1},
      filmDamageDustSize: {type: 'number', default: 1.0, min: 0.5, max: 3},
      filmDamageScratchWidth: {type: 'number', default: 0.5, min: 0.1, max: 1},
      
      // Radial blur settings
      radialBlurEnabled: {type: 'boolean', default: false},
      radialBlurStrength: {type: 'number', default: 0.3, min: 0, max: 1},
      radialBlurCenterX: {type: 'number', default: 0.5, min: 0, max: 1},
      radialBlurCenterY: {type: 'number', default: 0.5, min: 0, max: 1},
      
      // Film emulation settings
      filmEmulationEnabled: {type: 'boolean', default: false},
      filmEmulationType: {type: 'number', default: 1, min: 0, max: 3},
      filmEmulationIntensity: {type: 'number', default: 1.0, min: 0, max: 1},
      
      // Cinemascope letterbox settings
      cinemascopeEnabled: {type: 'boolean', default: false},
      cinemascopeRatio: {type: 'number', default: 2.35, min: 1.33, max: 2.76},
      cinemascopeOpacity: {type: 'number', default: 1.0, min: 0, max: 1}
    },
  
    init: function() {
      // Get scene elements
      this.camera = this.el.getObject3D('camera');
      this.scene = this.el.sceneEl.object3D;
      this.renderer = this.el.sceneEl.renderer;
  
      // Store original render method immediately
      this.originalRender = this.renderer.render;
  
      // Initialize performance monitor
      this.performanceMonitor = Object.create(PerformanceMonitor);
      this.performanceMonitor.constructor();
      this.performanceMonitor.targetFrameTime = 1000 / this.data.targetFPS;
      this.performanceMonitor.currentQuality = this.data.quality;
      
      // Create DOM elements for letterbox and vignette
      this.letterboxTop = null;
      this.letterboxBottom = null;
      this.vignette = null;
      if (this.data.letterboxEnabled) {
        this.createLetterbox();
      }
      if (this.data.vignetteEnabled) {
        this.createVignette();
      }
  
      // Setup post-processing pipeline
      this.setupPostProcessing();
      
      // Override render function
      this.setupRenderOverride();
      
      // Handle window resize
      this.onResize = this.onResize.bind(this);
      window.addEventListener('resize', this.onResize);
      
      // Initialize render targets for motion blur
      if (this.data.motionBlurEnabled) {
        this.initMotionBlur();
      }
  
      // Set initial effect state
      this.updateEffect(this.data.active);
      
      // Setup quality presets based on initial settings
      this.setupQualityPresets();
      this.applyQualityPreset(this.data.quality);
      
      // Bind animation frame handler for performance monitoring
      this.boundAnimationFrame = this.onAnimationFrame.bind(this);
      if (this.data.adaptiveQuality) {
        this.startPerformanceMonitoring();
      }
    },
  
    setupPostProcessing: function() {
      const pixelRatio = this.renderer.getPixelRatio();
      const size = this.renderer.getSize(new THREE.Vector2());
      const renderWidth = Math.floor(size.width * this.data.renderScale);
      const renderHeight = Math.floor(size.height * this.data.renderScale);
  
      console.log(`Setting up post-processing with resolution: ${renderWidth}x${renderHeight} (scale: ${this.data.renderScale})`);
  
      // Create render target options
      const rtOptions = {
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        format: THREE.RGBAFormat,
        stencilBuffer: false
      };
      
      // Add MSAA if enabled
      if (this.data.aaMethod === 'msaa') {
        rtOptions.samples = this.data.msaaSamples;
      }
      
      // Ensure we have a properly sized render target
      const renderTarget = new THREE.WebGLRenderTarget(renderWidth, renderHeight, rtOptions);
      
      // Create effect composer with our own renderer reference to prevent recursion
      this.composer = new EffectComposer(this.renderer, renderTarget);
      // Store original render method from the composer's renderer
      this.composer._originalRendererRender = this.composer.renderer.render;
  
      // Basic render pass
      this.renderPass = new RenderPass(this.scene, this.camera);
      this.composer.addPass(this.renderPass);
  
      // Add temporal anti-aliasing if enabled
      if (this.data.aaMethod === 'taa') {
        this.taaPass = new TAARenderPass(this.scene, this.camera);
        this.taaPass.enabled = true;
        this.composer.addPass(this.taaPass);
      }
  
      // Add FXAA if enabled
      if (this.data.aaMethod === 'fxaa') {
        this.fxaaPass = new ShaderPass(FXAAShader);
        this.fxaaPass.material.uniforms['resolution'].value = new THREE.Vector2();
        this.fxaaPass.material.uniforms['fxaaQualityRcpFrame'] = { value: new THREE.Vector2() };
        this.fxaaPass.enabled = true;
        this.composer.addPass(this.fxaaPass);
        this.updateFXAA();
      }
  
      // Add enhanced sharpening pass
      if (this.data.sharpeningEnabled) {
        this.sharpenPass = new ShaderPass(EnhancedCASShader);
        this.sharpenPass.uniforms.sharpness.value = this.data.sharpeningStrength;
        this.sharpenPass.uniforms.denoise.value = this.data.sharpeningDenoise;
        this.sharpenPass.uniforms.edgeDetection.value = this.data.sharpeningEdgeDetection;
        this.sharpenPass.enabled = this.data.sharpeningEnabled;
        this.composer.addPass(this.sharpenPass);
      }
  
      // Add SMAA if enabled
      if (this.data.aaMethod === 'smaa') {
        const width = this.el.sceneEl.canvas.width * pixelRatio;
        const height = this.el.sceneEl.canvas.height * pixelRatio;
        this.smaaPass = new SMAAPass(width, height);
        this.smaaPass.enabled = true;
        this.composer.addPass(this.smaaPass);
      }
  
      // Add bloom pass
      if (this.data.bloomEnabled) {
        this.bloomPass = new UnrealBloomPass(
          new THREE.Vector2(renderWidth, renderHeight),
          this.data.bloomStrength,
          this.data.bloomRadius,
          this.data.bloomThreshold
        );
        this.bloomPass.enabled = this.data.bloomEnabled;
        this.composer.addPass(this.bloomPass);
      }
  
      // Add enhanced color grading pass
      if (this.data.colorGradingEnabled) {
        this.colorGradingPass = new ShaderPass(ColorGradingShader);
        this.colorGradingPass.uniforms.brightness.value = this.data.colorBrightness;
        this.colorGradingPass.uniforms.contrast.value = this.data.colorContrast;
        this.colorGradingPass.uniforms.saturation.value = this.data.colorSaturation;
        this.colorGradingPass.uniforms.vibrance.value = this.data.colorVibrance;
        this.colorGradingPass.uniforms.shadows.value = this.data.colorShadows;
        this.colorGradingPass.uniforms.highlights.value = this.data.colorHighlights;
        this.colorGradingPass.uniforms.shadowsTint.value = new THREE.Color(this.data.colorShadowTint);
        this.colorGradingPass.uniforms.highlightsTint.value = new THREE.Color(this.data.colorHighlightTint);
        this.colorGradingPass.uniforms.lutEnabled.value = this.data.lutEnabled;
        
        // Set up LUT texture if provided
        if (this.data.lutEnabled && this.data.lutTexture) {
          const textureLoader = new THREE.TextureLoader();
          textureLoader.load(this.data.lutTexture, (texture) => {
            this.colorGradingPass.uniforms.tLUT.value = texture;
          });
        }
        
        this.colorGradingPass.enabled = this.data.colorGradingEnabled;
        this.composer.addPass(this.colorGradingPass);
      }
  
      // Setup DOF passes - using multi-pass approach for better performance
      if (this.data.dofEnabled) {
        this.setupEnhancedDOF(renderWidth, renderHeight);
      }
      
      // Add film grain pass if enabled
      if (this.data.filmGrainEnabled) {
        this.filmGrainPass = new ShaderPass(FilmGrainShader);
        this.filmGrainPass.uniforms.amount.value = this.data.filmGrainAmount;
        this.filmGrainPass.uniforms.speed.value = this.data.filmGrainSpeed;
        this.filmGrainPass.enabled = this.data.filmGrainEnabled;
        this.composer.addPass(this.filmGrainPass);
      }
      
      // Add chromatic aberration pass if enabled
      if (this.data.chromaticAberrationEnabled) {
        this.chromaticAberrationPass = new ShaderPass(ChromaticAberrationShader);
        this.chromaticAberrationPass.uniforms.amount.value = this.data.chromaticAberrationAmount;
        this.chromaticAberrationPass.uniforms.radialFactor.value = this.data.chromaticAberrationRadialFactor;
        this.chromaticAberrationPass.enabled = this.data.chromaticAberrationEnabled;
        this.composer.addPass(this.chromaticAberrationPass);
      }
  
      // Add lens distortion pass if enabled
      if (this.data.lensDistortionEnabled) {
        this.lensDistortionPass = new ShaderPass(LensDistortionShader);
        this.lensDistortionPass.uniforms.strength.value = this.data.lensDistortionStrength;
        this.lensDistortionPass.uniforms.cylindricalRatio.value = this.data.lensDistortionCylindricalRatio;
        this.lensDistortionPass.enabled = this.data.lensDistortionEnabled;
        this.composer.addPass(this.lensDistortionPass);
      }
      
      // Add anamorphic flare pass if enabled
      if (this.data.anamorphicFlareEnabled) {
        this.anamorphicFlarePass = new ShaderPass(AnamorphicFlareShader);
        this.anamorphicFlarePass.uniforms.intensity.value = this.data.anamorphicFlareIntensity;
        this.anamorphicFlarePass.uniforms.threshold.value = this.data.anamorphicFlareThreshold;
        this.anamorphicFlarePass.uniforms.color.value = new THREE.Vector3(
          parseInt(this.data.anamorphicFlareColor.slice(1, 3), 16) / 255,
          parseInt(this.data.anamorphicFlareColor.slice(3, 5), 16) / 255,
          parseInt(this.data.anamorphicFlareColor.slice(5, 7), 16) / 255
        );
        this.anamorphicFlarePass.uniforms.stretch.value = this.data.anamorphicFlareStretch;
        this.anamorphicFlarePass.enabled = this.data.anamorphicFlareEnabled;
        this.composer.addPass(this.anamorphicFlarePass);
      }
      
      // Add film damage pass if enabled
      if (this.data.filmDamageEnabled) {
        this.filmDamagePass = new ShaderPass(FilmDamageShader);
        this.filmDamagePass.uniforms.scratches.value = this.data.filmDamageScratchAmount;
        this.filmDamagePass.uniforms.dust.value = this.data.filmDamageDustAmount;
        this.filmDamagePass.uniforms.dustSize.value = this.data.filmDamageDustSize;
        this.filmDamagePass.uniforms.scratchWidth.value = this.data.filmDamageScratchWidth;
        this.filmDamagePass.enabled = this.data.filmDamageEnabled;
        this.composer.addPass(this.filmDamagePass);
      }
      
      // Add radial blur pass if enabled
      if (this.data.radialBlurEnabled) {
        this.radialBlurPass = new ShaderPass(RadialBlurShader);
        this.radialBlurPass.uniforms.strength.value = this.data.radialBlurStrength;
        this.radialBlurPass.uniforms.centerX.value = this.data.radialBlurCenterX;
        this.radialBlurPass.uniforms.centerY.value = this.data.radialBlurCenterY;
        this.radialBlurPass.enabled = this.data.radialBlurEnabled;
        this.composer.addPass(this.radialBlurPass);
      }
      
      // Add film emulation pass if enabled
      if (this.data.filmEmulationEnabled) {
        this.filmEmulationPass = new ShaderPass(FilmEmulationShader);
        this.filmEmulationPass.uniforms.filmType.value = this.data.filmEmulationType;
        this.filmEmulationPass.uniforms.intensity.value = this.data.filmEmulationIntensity;
        this.filmEmulationPass.enabled = this.data.filmEmulationEnabled;
        this.composer.addPass(this.filmEmulationPass);
      }
      
      // Add cinemascope letterbox pass if enabled
      if (this.data.cinemascopeEnabled) {
        this.cinemascopePass = new ShaderPass(CinemascopeLetterboxShader);
        this.cinemascopePass.uniforms.aspectRatio.value = this.data.cinemascopeRatio;
        this.cinemascopePass.uniforms.opacity.value = this.data.cinemascopeOpacity;
        this.cinemascopePass.enabled = this.data.cinemascopeEnabled;
        this.composer.addPass(this.cinemascopePass);
      }
      
      // Initialize motion blur if enabled
      if (this.data.motionBlurEnabled) {
        this.initMotionBlur();
      }
  
      // Add enhanced vignette pass (shader-based for better quality)
      if (this.data.vignetteEnabled) {
        this.vignettePass = new ShaderPass(EnhancedVignetteShader);
        this.vignettePass.uniforms.offset.value = 0.95;
        this.vignettePass.uniforms.darkness.value = this.data.vignetteStrength;
        this.vignettePass.uniforms.vignetteColor.value = new THREE.Vector3(
          parseInt(this.data.vignetteColor.slice(1, 3), 16) / 255,
          parseInt(this.data.vignetteColor.slice(3, 5), 16) / 255,
          parseInt(this.data.vignetteColor.slice(5, 7), 16) / 255
        );
        this.vignettePass.uniforms.cornerWeight.value = this.data.vignetteCornerWeight;
        this.vignettePass.enabled = this.data.vignetteEnabled;
        this.composer.addPass(this.vignettePass);
      }
  
      // Create a direct copy shader for the final pass without any blending or scaling
      this.finalPass = new ShaderPass({
        uniforms: {
          tDiffuse: { value: null }
        },
        vertexShader: `
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          uniform sampler2D tDiffuse;
          varying vec2 vUv;
          void main() {
            gl_FragColor = texture2D(tDiffuse, vUv);
          }
        `
      });
      
      // This is critical - we need to make sure the final pass is not auto-sized
      this.finalPass.setSize = function(width, height) {
        // Override to prevent automatic resizing of this pass
        console.log("Keeping final pass at original render target size");
      };
      
      this.composer.addPass(this.finalPass);
      
      // Log the actual resolution being used
      console.log(`Post-processing setup complete. Render resolution: ${renderWidth}x${renderHeight}`);
    },
  
    // Setup enhanced DOF using multiple passes for better performance
    setupEnhancedDOF: function(width, height) {
      // Create render targets for DOF blur
      this.dofRenderTarget = new THREE.WebGLRenderTarget(width * 0.5, height * 0.5, {
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        format: THREE.RGBAFormat,
        stencilBuffer: false
      });
      
      // Create blur passes for DOF - two-pass Gaussian blur for efficiency
      this.dofHorizontalBlurPass = new ShaderPass({
        uniforms: {
          tDiffuse: { value: null },
          h: { value: 1.0 / (width * 0.5) }
        },
        vertexShader: `
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          uniform sampler2D tDiffuse;
          uniform float h;
          varying vec2 vUv;
          
          void main() {
            vec4 sum = vec4(0.0);
            
            // Gaussian weights
            sum += texture2D(tDiffuse, vec2(vUv.x - 4.0*h, vUv.y)) * 0.051;
            sum += texture2D(tDiffuse, vec2(vUv.x - 3.0*h, vUv.y)) * 0.0918;
            sum += texture2D(tDiffuse, vec2(vUv.x - 2.0*h, vUv.y)) * 0.12245;
            sum += texture2D(tDiffuse, vec2(vUv.x - 1.0*h, vUv.y)) * 0.1531;
            sum += texture2D(tDiffuse, vec2(vUv.x, vUv.y)) * 0.1633;
            sum += texture2D(tDiffuse, vec2(vUv.x + 1.0*h, vUv.y)) * 0.1531;
            sum += texture2D(tDiffuse, vec2(vUv.x + 2.0*h, vUv.y)) * 0.12245;
            sum += texture2D(tDiffuse, vec2(vUv.x + 3.0*h, vUv.y)) * 0.0918;
            sum += texture2D(tDiffuse, vec2(vUv.x + 4.0*h, vUv.y)) * 0.051;
            
            gl_FragColor = sum;
          }
        `
      });
      
      this.dofVerticalBlurPass = new ShaderPass({
        uniforms: {
          tDiffuse: { value: null },
          v: { value: 1.0 / (height * 0.5) }
        },
        vertexShader: `
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          uniform sampler2D tDiffuse;
          uniform float v;
          varying vec2 vUv;
          
          void main() {
            vec4 sum = vec4(0.0);
            
            // Gaussian weights
            sum += texture2D(tDiffuse, vec2(vUv.x, vUv.y - 4.0*v)) * 0.051;
            sum += texture2D(tDiffuse, vec2(vUv.x, vUv.y - 3.0*v)) * 0.0918;
            sum += texture2D(tDiffuse, vec2(vUv.x, vUv.y - 2.0*v)) * 0.12245;
            sum += texture2D(tDiffuse, vec2(vUv.x, vUv.y - 1.0*v)) * 0.1531;
            sum += texture2D(tDiffuse, vec2(vUv.x, vUv.y)) * 0.1633;
            sum += texture2D(tDiffuse, vec2(vUv.x, vUv.y + 1.0*v)) * 0.1531;
            sum += texture2D(tDiffuse, vec2(vUv.x, vUv.y + 2.0*v)) * 0.12245;
            sum += texture2D(tDiffuse, vec2(vUv.x, vUv.y + 3.0*v)) * 0.0918;
            sum += texture2D(tDiffuse, vec2(vUv.x, vUv.y + 4.0*v)) * 0.051;
            
            gl_FragColor = sum;
          }
        `
      });
      
      // Create DOF composer for blur passes - with extra safety against render recursion
      this.dofComposer = new EffectComposer(this.renderer, this.dofRenderTarget);
      // Store original render method for safety
      this.dofComposer._originalRendererRender = this.dofComposer.renderer.render;
      
      // Add basic render pass
      this.dofComposer.addPass(new RenderPass(this.scene, this.camera));
      this.dofComposer.addPass(this.dofHorizontalBlurPass);
      this.dofComposer.addPass(this.dofVerticalBlurPass);
      
      // Create enhanced DOF pass
      this.enhancedDOFPass = new ShaderPass(EnhancedDOFShader);
      this.enhancedDOFPass.uniforms.tBlurred.value = this.dofRenderTarget.texture;
      this.enhancedDOFPass.uniforms.focusDistance.value = this.data.dofFocus;
      this.enhancedDOFPass.uniforms.aperture.value = this.data.dofAperture;
      this.enhancedDOFPass.uniforms.nearTransition.value = this.data.dofNearTransition;
      this.enhancedDOFPass.uniforms.farTransition.value = this.data.dofFarTransition;
      this.enhancedDOFPass.uniforms.nearBlur.value = this.data.dofNearBlur;
      this.enhancedDOFPass.uniforms.farBlur.value = this.data.dofFarBlur;
      this.enhancedDOFPass.uniforms.maxBlur.value = this.data.dofMaxBlur;
      this.enhancedDOFPass.uniforms.bokehShape.value = this.getBokehShapeValue();
      this.enhancedDOFPass.uniforms.bokehRotation.value = this.data.dofBokehRotation;
      this.enhancedDOFPass.uniforms.depthRange.value = 1000; // Adjust based on scene scale
      this.enhancedDOFPass.enabled = this.data.dofEnabled;
      
      // Add to main composer
      this.composer.addPass(this.enhancedDOFPass);
    },
  
    setupRenderOverride: function() {
      const sceneEl = this.el.sceneEl;
      const renderer = sceneEl.renderer;
      
      // Store original render method
      this.originalRender = renderer.render;
      this.isRendering = false;
      
      // Create a direct reference to the original render function that won't be overridden
      const originalRenderFn = this.originalRender.bind(renderer);
      
      // Override renderer.render
      renderer.render = (scene, camera) => {
        // If we're already in a render call or effects aren't active, use original render
        if (this.isRendering || !this.composer || !this.data.active) {
          originalRenderFn(scene, camera);
          return;
        }
        
        // Set flag to prevent recursion
        this.isRendering = true;
        
        try {
          // For DOF, we need to render blur first
          if (this.data.dofEnabled && this.dofComposer) {
            // Save current render target
            const currentRenderTarget = renderer.getRenderTarget();
            
            // Use the original render function directly for DOF passes
            this.dofComposer._oldRender = this.dofComposer.renderer.render;
            this.dofComposer.renderer.render = originalRenderFn;
            
            // Render DOF
            this.dofComposer.render();
            
            // Restore original renderer's render target
            renderer.setRenderTarget(currentRenderTarget);
            
            // Restore composer's render method
            this.dofComposer.renderer.render = this.dofComposer._oldRender;
          }
          
          // Animate film grain time if enabled
          if (this.data.filmGrainEnabled && this.filmGrainPass) {
            this.filmGrainPass.uniforms.time.value = performance.now() * 0.001;
          }
          
          // Animate film damage if enabled
          if (this.data.filmDamageEnabled && this.filmDamagePass) {
            this.filmDamagePass.uniforms.time.value = performance.now() * 0.001;
          }
          
          // Handle motion blur if enabled
          if (this.data.motionBlurEnabled && this.motionBlurPass && this.prevFrameRT) {
            // Save current render target
            const currentRenderTarget = renderer.getRenderTarget();
            
            // Override composer's render method to use original renderer
            this.composer._oldRender = this.composer.renderer.render;
            this.composer.renderer.render = originalRenderFn;
            
            // Render with composer
            this.composer.render();
            
            // Copy current frame to previous frame RT for next frame
            renderer.setRenderTarget(this.prevFrameRT);
            renderer.clear();
            originalRenderFn(scene, camera);
            
            // Restore render target
            renderer.setRenderTarget(currentRenderTarget);
            
            // Restore composer's render method
            this.composer.renderer.render = this.composer._oldRender;
          } else {
            // Standard render - override composer's render method to use original renderer
            this.composer._oldRender = this.composer.renderer.render;
            this.composer.renderer.render = originalRenderFn;
            
            // Render the composer
            this.composer.render();
            
            // Restore composer's render method
            this.composer.renderer.render = this.composer._oldRender;
          }
        } catch (error) {
          console.error('Error in cinematic camera rendering:', error);
          // Fall back to original render if something goes wrong
          originalRenderFn(scene, camera);
        } finally {
          // Always reset the rendering flag
          this.isRendering = false;
        }
      };
      
      // Render start listener for A-Frame
      this.renderStartListener = () => {
        if (this.composer && this.data.active && !this.isRendering) {
          // Use the same implementation as above but without the motion blur handling
          this.isRendering = true;
          
          try {
            // For DOF, we need to render blur first
            if (this.data.dofEnabled && this.dofComposer) {
              const currentRenderTarget = renderer.getRenderTarget();
              
              this.dofComposer._oldRender = this.dofComposer.renderer.render;
              this.dofComposer.renderer.render = originalRenderFn;
              
              this.dofComposer.render();
              
              renderer.setRenderTarget(currentRenderTarget);
              this.dofComposer.renderer.render = this.dofComposer._oldRender;
            }
            
            this.composer._oldRender = this.composer.renderer.render;
            this.composer.renderer.render = originalRenderFn;
            
            this.composer.render();
            
            this.composer.renderer.render = this.composer._oldRender;
          } catch (error) {
            console.error('Error in cinematic camera renderStart:', error);
            originalRenderFn(this.scene, this.camera);
          } finally {
            this.isRendering = false;
          }
        }
      };
      
      sceneEl.addEventListener('renderstart', this.renderStartListener);
    },
  
    // Initialize motion blur by creating render target for previous frame
    initMotionBlur: function() {
      const size = this.renderer.getSize(new THREE.Vector2());
      const pixelRatio = this.renderer.getPixelRatio();
      const renderWidth = size.width * this.data.renderScale;
      const renderHeight = size.height * this.data.renderScale;
      
      // Create render target for previous frame
      this.prevFrameRT = new THREE.WebGLRenderTarget(renderWidth, renderHeight, {
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        format: THREE.RGBAFormat,
        stencilBuffer: false
      });
      
      // Initialize motion blur pass
      this.motionBlurPass = new ShaderPass(MotionBlurShader);
      this.motionBlurPass.uniforms.tPrevious.value = this.prevFrameRT.texture;
      this.motionBlurPass.uniforms.strength.value = this.data.motionBlurStrength;
      this.motionBlurPass.enabled = this.data.motionBlurEnabled;
      
      if (this.composer) {
        this.composer.addPass(this.motionBlurPass);
      }
    },
  
    // Update render scale with safety checks for high resolution
    updateRenderScale: function() {
      if (this.composer) {
        const size = this.renderer.getSize(new THREE.Vector2());
        const pixelRatio = this.renderer.getPixelRatio();
        
        // Apply safety checks for very high resolution rendering
        let renderScale = this.data.renderScale;
        const totalPixels = size.width * size.height * renderScale * renderScale;
        const MAX_SAFE_PIXELS = 33177600; // 8K equivalent (7680 × 4320)
        
        // Calculate new dimensions
        const renderWidth = Math.floor(size.width * renderScale);
        const renderHeight = Math.floor(size.height * renderScale);
        
        console.log(`Updating render scale to ${renderScale} (${renderWidth}x${renderHeight})`);
        
        // If we're exceeding 8K equivalent pixels, warn the user but continue
        if (totalPixels > MAX_SAFE_PIXELS) {
          console.warn(`Very high resolution rendering requested: ${renderWidth}x${renderHeight}. This may impact performance significantly.`);
        }
  
        // Handle high resolution carefully
        try {
          // Create a new render target at the desired resolution
          const newRenderTarget = new THREE.WebGLRenderTarget(renderWidth, renderHeight, {
            minFilter: THREE.LinearFilter,
            magFilter: THREE.LinearFilter,
            format: THREE.RGBAFormat,
            stencilBuffer: false,
            samples: this.data.aaMethod === 'msaa' ? this.data.msaaSamples : 0
          });
          
          // For EffectComposer, we need to dispose the old one and create a new one
          const oldPasses = [];
          if (this.composer) {
            // Save all existing passes
            for (let i = 0; i < this.composer.passes.length; i++) {
              oldPasses.push(this.composer.passes[i]);
            }
            this.composer.dispose();
          }
          
          // Create new composer with new render target
          this.composer = new EffectComposer(this.renderer, newRenderTarget);
          
          // Add back all the passes
          for (let i = 0; i < oldPasses.length; i++) {
            this.composer.addPass(oldPasses[i]);
          }
          
          // Set size on the composer
          this.composer.setSize(renderWidth, renderHeight);
          
          // Update DOF composer if it exists - with half resolution for performance
          if (this.dofComposer) {
            const dofWidth = Math.floor(renderWidth * 0.5);
            const dofHeight = Math.floor(renderHeight * 0.5);
            
            const dofRenderTarget = new THREE.WebGLRenderTarget(dofWidth, dofHeight, {
              minFilter: THREE.LinearFilter,
              magFilter: THREE.LinearFilter,
              format: THREE.RGBAFormat,
              stencilBuffer: false
            });
            
            // Save old passes
            const oldDOFPasses = [];
            for (let i = 0; i < this.dofComposer.passes.length; i++) {
              oldDOFPasses.push(this.dofComposer.passes[i]);
            }
            this.dofComposer.dispose();
            
            // Create new DOF composer
            this.dofComposer = new EffectComposer(this.renderer, dofRenderTarget);
            
            // Add back passes
            for (let i = 0; i < oldDOFPasses.length; i++) {
              this.dofComposer.addPass(oldDOFPasses[i]);
            }
            
            this.dofComposer.setSize(dofWidth, dofHeight);
            
            // Update DOF-related uniforms
            if (this.dofHorizontalBlurPass) {
              this.dofHorizontalBlurPass.uniforms.h.value = 1.0 / dofWidth;
            }
            
            if (this.dofVerticalBlurPass) {
              this.dofVerticalBlurPass.uniforms.v.value = 1.0 / dofHeight;
            }
            
            // Update the dofRenderTarget reference
            this.dofRenderTarget = dofRenderTarget;
            
            // Update enhanced DOF pass with the new blurred texture
            if (this.enhancedDOFPass) {
              this.enhancedDOFPass.uniforms.tBlurred.value = this.dofRenderTarget.texture;
            }
          }
          
          // Update motion blur render target if it exists
          if (this.prevFrameRT) {
            // Create a new render target for previous frames
            const oldPrevFrameRT = this.prevFrameRT;
            this.prevFrameRT = new THREE.WebGLRenderTarget(renderWidth, renderHeight, {
              minFilter: THREE.LinearFilter,
              magFilter: THREE.LinearFilter,
              format: THREE.RGBAFormat,
              stencilBuffer: false
            });
            
            // Update the texture reference in the motion blur pass
            if (this.motionBlurPass) {
              this.motionBlurPass.uniforms.tPrevious.value = this.prevFrameRT.texture;
            }
            
            // Dispose of old render target
            oldPrevFrameRT.dispose();
          }
          
          // Update size of all passes
          this.updatePassesSizesForNewResolution(renderWidth, renderHeight);
          
          // Set up render override again since we have a new composer
          this.setupRenderOverride();
          
          console.log(`Render scale updated successfully to ${renderScale} (${renderWidth}x${renderHeight})`);
        } catch (error) {
          console.error("Error updating render scale:", error);
          // Set a safe default value
          this.data.renderScale = 1.0;
          console.warn("Falling back to default render scale (1.0) due to error");
          
          // Try to recreate everything with default settings
          this.rebuildPostProcessing();
        }
      }
    },
  
    // Helper method to update all pass sizes for new resolution
    updatePassesSizesForNewResolution: function(width, height) {
      // For anti-aliasing passes
      if (this.fxaaPass) {
        const pixelRatio = this.renderer.getPixelRatio();
        this.fxaaPass.material.uniforms['resolution'].value.set(
          1.0 / (width * pixelRatio), 
          1.0 / (height * pixelRatio)
        );
        
        // Update FXAA quality
        let qualityValue;
        switch (this.data.fxaaLevel) {
          case 4: qualityValue = 1/4; break;
          case 8: qualityValue = 1/8; break;
          case 16: qualityValue = 1/16; break;
          default: qualityValue = 1/4;
        }
        this.fxaaPass.material.uniforms['fxaaQualityRcpFrame'].value.set(qualityValue, qualityValue);
        console.log(`FXAA pass updated for resolution ${width}x${height}`);
      }
      
      // For SMAA pass
      if (this.smaaPass) {
        this.smaaPass.setSize(width, height);
        console.log(`SMAA pass updated for resolution ${width}x${height}`);
      }
      
      // For bloom pass
      if (this.bloomPass) {
        this.bloomPass.setSize(width, height);
        console.log(`Bloom pass updated for resolution ${width}x${height}`);
      }
      
      // For TAA pass
      if (this.taaPass) {
        this.taaPass.setSize(width, height);
        console.log(`TAA pass updated for resolution ${width}x${height}`);
      }
      
      // For bokeh pass
      if (this.bokehPass) {
        this.bokehPass.setSize(width, height);
        console.log(`Bokeh pass updated for resolution ${width}x${height}`);
      }
      
      // Make sure other passes that need specific sizing are updated
      if (this.colorGradingPass) {
        this.colorGradingPass.setSize(width, height);
        console.log(`Color grading pass updated for resolution ${width}x${height}`);
      }
      
      if (this.sharpenPass) {
        this.sharpenPass.setSize(width, height);
        console.log(`Sharpen pass updated for resolution ${width}x${height}`);
      }
      
      if (this.filmGrainPass) {
        this.filmGrainPass.setSize(width, height);
        console.log(`Film grain pass updated for resolution ${width}x${height}`);
      }
      
      if (this.chromaticAberrationPass) {
        this.chromaticAberrationPass.setSize(width, height);
        console.log(`Chromatic aberration pass updated for resolution ${width}x${height}`);
      }
      
      if (this.lensDistortionPass) {
        this.lensDistortionPass.setSize(width, height);
        console.log(`Lens distortion pass updated for resolution ${width}x${height}`);
      }
      
      if (this.motionBlurPass) {
        this.motionBlurPass.setSize(width, height);
        console.log(`Motion blur pass updated for resolution ${width}x${height}`);
      }
      
      if (this.vignettePass) {
        this.vignettePass.setSize(width, height);
        console.log(`Vignette pass updated for resolution ${width}x${height}`);
      }
      
      // New passes
      if (this.anamorphicFlarePass) {
        this.anamorphicFlarePass.setSize(width, height);
        console.log(`Anamorphic flare pass updated for resolution ${width}x${height}`);
      }
      
      if (this.filmDamagePass) {
        this.filmDamagePass.setSize(width, height);
        console.log(`Film damage pass updated for resolution ${width}x${height}`);
      }
      
      if (this.radialBlurPass) {
        this.radialBlurPass.setSize(width, height);
        console.log(`Radial blur pass updated for resolution ${width}x${height}`);
      }
      
      if (this.filmEmulationPass) {
        this.filmEmulationPass.setSize(width, height);
        console.log(`Film emulation pass updated for resolution ${width}x${height}`);
      }
      
      if (this.cinemascopePass) {
        this.cinemascopePass.setSize(width, height);
        console.log(`Cinemascope pass updated for resolution ${width}x${height}`);
      }
      
      if (this.finalPass) {
        // Final pass should NOT be resized - it needs to maintain the high resolution
        console.log(`Final pass kept at original render target size (${width}x${height})`);
      }
    },
  
    // Handle window resize
    onResize: function() {
      if (!this.composer) return;
  
      const canvas = this.el.sceneEl.canvas;
      const pixelRatio = this.renderer.getPixelRatio();
      const width = canvas.clientWidth * pixelRatio * this.data.renderScale;
      const height = canvas.clientHeight * pixelRatio * this.data.renderScale;
  
      // Update composer and all passes
      this.composer.setSize(width, height);
  
      // Update passes directly without circular references
      if (this.smaaPass) {
        this.smaaPass.setSize(width, height);
      }
      if (this.fxaaPass) {
        this.updateFXAA();
      }
      if (this.bloomPass) {
        this.bloomPass.setSize(width, height);
      }
      if (this.bokehPass) {
        this.bokehPass.setSize(width, height);
      }
      
      // Update DOF-related passes
      if (this.dofComposer) {
        this.dofComposer.setSize(width * 0.5, height * 0.5);
      }
      if (this.dofRenderTarget) {
        this.dofRenderTarget.setSize(width * 0.5, height * 0.5);
        if (this.dofHorizontalBlurPass) {
          this.dofHorizontalBlurPass.uniforms.h.value = 1.0 / (width * 0.5);
        }
        if (this.dofVerticalBlurPass) {
          this.dofVerticalBlurPass.uniforms.v.value = 1.0 / (height * 0.5);
        }
      }
      
      // Update upscale pass
      if (this.upscalePass) {
        this.upscalePass.uniforms.resolution.value.set(
          canvas.clientWidth * pixelRatio, 
          canvas.clientHeight * pixelRatio
        );
        this.upscalePass.uniforms.renderResolution.value.set(width, height);
      }
  
      // Update camera aspect ratio
      const camera = this.el.getObject3D('camera');
      if (camera) {
        camera.aspect = canvas.clientWidth / canvas.clientHeight;
        camera.updateProjectionMatrix();
      }
  
      // Force a single render to update the view
      this.el.sceneEl.renderer.render(this.el.sceneEl.object3D, camera);
    },
  
    // Get bokeh shape value from string setting
    getBokehShapeValue: function() {
      switch(this.data.dofBokehShape) {
        case 'hexagonal': return 1;
        case 'octagonal': return 2;
        default: return 0; // circular
      }
    },
  
    // Update FXAA shader parameters
    updateFXAA: function() {
      if (this.fxaaPass && this.fxaaPass.material) {
        this.fxaaPass.enabled = this.data.aaMethod === 'fxaa';
        
        if (this.data.aaMethod === 'fxaa') {
          const pixelRatio = this.renderer.getPixelRatio();
          const width = this.el.sceneEl.canvas.width;
          const height = this.el.sceneEl.canvas.height;
          
          this.fxaaPass.material.uniforms['resolution'].value.set(1 / (width * pixelRatio), 1 / (height * pixelRatio));
          
          // Adjust FXAA quality based on level
          let qualityValue;
          switch (this.data.fxaaLevel) {
            case 4:
              qualityValue = 1/4;
              break;
            case 8:
              qualityValue = 1/8;
              break;
            case 16:
              qualityValue = 1/16;
              break;
            default:
              qualityValue = 1/4;
          }
          this.fxaaPass.material.uniforms['fxaaQualityRcpFrame'].value.set(qualityValue, qualityValue);
        }
      }
    },
  
    // Create DOM element for letterbox
    createLetterbox: function() {
      this.letterboxTop = document.createElement('a-plane');
      this.letterboxBottom = document.createElement('a-plane');
  
      const commonAttributes = {
        color: '#000',
        opacity: 1,
        position: '0 0 -0.1',
        rotation: '0 0 0',
        material: 'shader: flat',
        width: 2,
        height: this.data.letterboxHeight
      };
  
      Object.keys(commonAttributes).forEach(attr => {
        this.letterboxTop.setAttribute(attr, commonAttributes[attr]);
        this.letterboxBottom.setAttribute(attr, commonAttributes[attr]);
      });
  
      this.updateLetterbox();
  
      this.el.appendChild(this.letterboxTop);
      this.el.appendChild(this.letterboxBottom);
    },
  
    // Update letterbox position and size
    updateLetterbox: function() {
      if (this.letterboxTop && this.letterboxBottom) {
        this.letterboxTop.setAttribute('position', `0 ${0.5 + this.data.letterboxHeight / 2} -0.1`);
        this.letterboxBottom.setAttribute('position', `0 ${-0.5 - this.data.letterboxHeight / 2} -0.1`);
        this.letterboxTop.setAttribute('geometry', 'height', this.data.letterboxHeight);
        this.letterboxBottom.setAttribute('geometry', 'height', this.data.letterboxHeight);
      }
    },
  
    // Create DOM element for vignette
    createVignette: function() {
      this.vignette = document.createElement('a-entity');
      this.vignette.setAttribute('geometry', {
        primitive: 'plane',
        width: 2,
        height: 2
      });
      this.updateVignette();
      this.vignette.setAttribute('position', '0 0 -0.05');
      this.el.appendChild(this.vignette);
    },
  
    // Update vignette material
    updateVignette: function() {
      if (this.vignette) {
        this.vignette.setAttribute('material', {
          shader: 'flat',
          src: this.createVignetteTexture(),
          transparent: true,
          opacity: this.data.vignetteStrength
        });
      }
    },
  
    // Create vignette texture
    createVignetteTexture: function() {
      const canvas = document.createElement('canvas');
      canvas.width = 256;
      canvas.height = 256;
      const ctx = canvas.getContext('2d');
      
      const gradient = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
      gradient.addColorStop(0, 'rgba(0,0,0,0)');
      gradient.addColorStop(1, this.data.vignetteColor);
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 256, 256);
      
      return canvas.toDataURL();
    },
  
    // Setup quality presets
    setupQualityPresets: function() {
      this.qualityPresets = {
        low: {
          renderScale: 0.75,
          aaMethod: 'fxaa',
          fxaaLevel: 8,
          bloomEnabled: true,
          bloomStrength: 0.5,
          bloomRadius: 0.2,
          bloomThreshold: 0.9,
          dofEnabled: false,
          sharpeningEnabled: true,
          sharpeningStrength: 0.3,
          colorGradingEnabled: true,
          filmGrainEnabled: false,
          chromaticAberrationEnabled: false,
          lensDistortionEnabled: false,
          motionBlurEnabled: false,
          anamorphicFlareEnabled: false,
          filmDamageEnabled: false,
          radialBlurEnabled: false,
          filmEmulationEnabled: false,
          cinemascopeEnabled: false
        },
        medium: {
          renderScale: 1.0,
          aaMethod: 'fxaa',
          fxaaLevel: 4,
          bloomEnabled: true,
          bloomStrength: 0.7,
          bloomRadius: 0.25,
          bloomThreshold: 0.9,
          dofEnabled: true,
          dofMaxBlur: 0.5,
          sharpeningEnabled: true,
          sharpeningStrength: 0.4,
          colorGradingEnabled: true,
          filmGrainEnabled: false,
          chromaticAberrationEnabled: false,
          lensDistortionEnabled: false,
          motionBlurEnabled: false,
          anamorphicFlareEnabled: false,
          filmDamageEnabled: false,
          radialBlurEnabled: false,
          filmEmulationEnabled: true,
          filmEmulationType: 1, // Kodak look
          filmEmulationIntensity: 0.7,
          cinemascopeEnabled: false
        },
        high: {
          renderScale: 1.1,
          aaMethod: 'smaa',
          bloomEnabled: true,
          bloomStrength: 0.8,
          bloomRadius: 0.3,
          bloomThreshold: 0.9,
          dofEnabled: true,
          dofMaxBlur: 0.8,
          sharpeningEnabled: true,
          sharpeningStrength: 0.5,
          colorGradingEnabled: true,
          filmGrainEnabled: true,
          filmGrainAmount: 0.03,
          chromaticAberrationEnabled: true,
          chromaticAberrationAmount: 0.003,
          lensDistortionEnabled: false,
          motionBlurEnabled: false,
          anamorphicFlareEnabled: true,
          anamorphicFlareIntensity: 0.3,
          anamorphicFlareThreshold: 0.9,
          filmDamageEnabled: false,
          radialBlurEnabled: false,
          filmEmulationEnabled: true,
          filmEmulationType: 1, // Kodak look
          filmEmulationIntensity: 0.8,
          cinemascopeEnabled: true,
          cinemascopeRatio: 2.35,
          cinemascopeOpacity: 0.9
        },
        ultra: {
          renderScale: 1.5,  // Increased from 1.2 for better quality
          aaMethod: 'msaa',
          msaaSamples: 4,
          bloomEnabled: true,
          bloomStrength: 0.9,
          bloomRadius: 0.35,
          bloomThreshold: 0.85,
          dofEnabled: true,
          dofMaxBlur: 1.0,
          sharpeningEnabled: true,
          sharpeningStrength: 0.5,
          colorGradingEnabled: true,
          filmGrainEnabled: true,
          filmGrainAmount: 0.04,
          chromaticAberrationEnabled: true,
          chromaticAberrationAmount: 0.0015,  // Reduced from 0.005 to be more subtle
          chromaticAberrationRadialFactor: 0.3,  // Reduced from 0.5 for more controlled effect
          lensDistortionEnabled: true,
          lensDistortionStrength: 0.1,
          motionBlurEnabled: true,
          motionBlurStrength: 0.3,
          anamorphicFlareEnabled: true,
          anamorphicFlareIntensity: 0.5,
          anamorphicFlareThreshold: 0.85,
          filmDamageEnabled: false,
          radialBlurEnabled: false,
          filmEmulationEnabled: true,
          filmEmulationType: 1, // Kodak look
          filmEmulationIntensity: 1.0,
          cinemascopeEnabled: true,
          cinemascopeRatio: 2.39,
          cinemascopeOpacity: 1.0
        }
      };
    },
  
    // Apply quality preset
    applyQualityPreset: function(quality) {
      if (quality === 'auto') {
        // Let performance monitor handle it
        return;
      }
      
      const preset = this.qualityPresets[quality];
      if (!preset) return;
      
      // Apply preset values
      Object.keys(preset).forEach(key => {
        this.data[key] = preset[key];
      });
      
      // Update everything
      this.updatePostProcessing();
    },
  
    // Start performance monitoring
    startPerformanceMonitoring: function() {
      this.performanceMonitorEnabled = true;
      this.performanceMonitor.reset();
      requestAnimationFrame(this.boundAnimationFrame);
    },
  
    // Stop performance monitoring
    stopPerformanceMonitoring: function() {
      this.performanceMonitorEnabled = false;
    },
  
    // Animation frame handler for performance monitoring
    onAnimationFrame: function() {
      if (!this.performanceMonitorEnabled) return;
      
      this.performanceMonitor.end();
      
      // Check if quality adjustment is needed
      if (this.data.adaptiveQuality && this.performanceMonitor.qualityAdjustmentNeeded) {
        this.adjustQualityBasedOnPerformance(this.performanceMonitor.qualityAdjustmentNeeded);
        this.performanceMonitor.qualityAdjustmentNeeded = false;
      }
      
      this.performanceMonitor.start();
      requestAnimationFrame(this.boundAnimationFrame);
    },
  
    // Adjust quality based on performance
    adjustQualityBasedOnPerformance: function(direction) {
      const qualityLevels = ['low', 'medium', 'high', 'ultra'];
      const currentLevel = this.performanceMonitor.getQualityLevel();
      const currentIndex = qualityLevels.indexOf(currentLevel);
      
      if (direction === 'down' && currentIndex > 0) {
        const newQuality = qualityLevels[currentIndex - 1];
        console.log(`Performance adjustment: Lowering quality to ${newQuality}`);
        this.applyQualityPreset(newQuality);
      } else if (direction === 'up' && currentIndex < qualityLevels.length - 1) {
        const newQuality = qualityLevels[currentIndex + 1];
        console.log(`Performance adjustment: Increasing quality to ${newQuality}`);
        this.applyQualityPreset(newQuality);
      }
      
      // Reset monitor after adjustment
      this.performanceMonitor.reset();
    },
  
    // Determine if post-processing pipeline needs complete rebuild
    needsRebuild: function(oldData) {
      // Check for changes that require pipeline rebuild
      return (
        this.data.aaMethod !== oldData.aaMethod ||
        this.data.dofEnabled !== oldData.dofEnabled ||
        this.data.sharpeningEnabled !== oldData.sharpeningEnabled ||
        this.data.colorGradingEnabled !== oldData.colorGradingEnabled ||
        this.data.bloomEnabled !== oldData.bloomEnabled ||
        this.data.filmGrainEnabled !== oldData.filmGrainEnabled ||
        this.data.chromaticAberrationEnabled !== oldData.chromaticAberrationEnabled ||
        this.data.lensDistortionEnabled !== oldData.lensDistortionEnabled ||
        this.data.motionBlurEnabled !== oldData.motionBlurEnabled ||
        this.data.anamorphicFlareEnabled !== oldData.anamorphicFlareEnabled ||
        this.data.filmDamageEnabled !== oldData.filmDamageEnabled ||
        this.data.radialBlurEnabled !== oldData.radialBlurEnabled ||
        this.data.filmEmulationEnabled !== oldData.filmEmulationEnabled ||
        this.data.cinemascopeEnabled !== oldData.cinemascopeEnabled
      );
    },
  
    // Rebuild the entire post-processing pipeline
    rebuildPostProcessing: function() {
      // Clean up existing pipeline
      if (this.composer) {
        this.composer.dispose();
      }
      if (this.dofComposer) {
        this.dofComposer.dispose();
      }
      
      // Rebuild
      this.setupPostProcessing();
      this.setupRenderOverride();
    },
  
    // Update individual post-processing settings
    updatePostProcessing: function() {
      // Update FXAA settings
      if (this.fxaaPass) {
        this.updateFXAA();
      }
      
      // Update SMAA settings
      if (this.smaaPass) {
        this.smaaPass.enabled = this.data.aaMethod === 'smaa';
      }
      
      // Update TAA settings
      if (this.taaPass) {
        this.taaPass.enabled = this.data.aaMethod === 'taa';
      }
      
      // Update bloom settings
      if (this.bloomPass) {
        this.bloomPass.enabled = this.data.bloomEnabled;
        this.bloomPass.strength = this.data.bloomStrength;
        this.bloomPass.radius = this.data.bloomRadius;
        this.bloomPass.threshold = this.data.bloomThreshold;
      }
      
      // Update enhanced DOF settings
      if (this.enhancedDOFPass) {
        this.enhancedDOFPass.enabled = this.data.dofEnabled;
        this.enhancedDOFPass.uniforms.focusDistance.value = this.data.dofFocus;
        this.enhancedDOFPass.uniforms.aperture.value = this.data.dofAperture;
        this.enhancedDOFPass.uniforms.maxBlur.value = this.data.dofMaxBlur;
        this.enhancedDOFPass.uniforms.nearTransition.value = this.data.dofNearTransition;
        this.enhancedDOFPass.uniforms.farTransition.value = this.data.dofFarTransition;
        this.enhancedDOFPass.uniforms.nearBlur.value = this.data.dofNearBlur;
        this.enhancedDOFPass.uniforms.farBlur.value = this.data.dofFarBlur;
        this.enhancedDOFPass.uniforms.bokehShape.value = this.getBokehShapeValue();
        this.enhancedDOFPass.uniforms.bokehRotation.value = this.data.dofBokehRotation;
      }
      
      // Update sharpening settings
      if (this.sharpenPass) {
        this.sharpenPass.enabled = this.data.sharpeningEnabled;
        this.sharpenPass.uniforms.sharpness.value = this.data.sharpeningStrength;
        this.sharpenPass.uniforms.denoise.value = this.data.sharpeningDenoise;
        this.sharpenPass.uniforms.edgeDetection.value = this.data.sharpeningEdgeDetection;
      }
      
      // Update color grading settings
      if (this.colorGradingPass) {
        this.colorGradingPass.enabled = this.data.colorGradingEnabled;
        this.colorGradingPass.uniforms.brightness.value = this.data.colorBrightness;
        this.colorGradingPass.uniforms.contrast.value = this.data.colorContrast;
        this.colorGradingPass.uniforms.saturation.value = this.data.colorSaturation;
        this.colorGradingPass.uniforms.vibrance.value = this.data.colorVibrance;
        this.colorGradingPass.uniforms.shadows.value = this.data.colorShadows;
        this.colorGradingPass.uniforms.highlights.value = this.data.colorHighlights;
        this.colorGradingPass.uniforms.shadowsTint.value = new THREE.Color(this.data.colorShadowTint);
        this.colorGradingPass.uniforms.highlightsTint.value = new THREE.Color(this.data.colorHighlightTint);
        this.colorGradingPass.uniforms.lutEnabled.value = this.data.lutEnabled;
      }
      
      // Update film grain settings
      if (this.filmGrainPass) {
        this.filmGrainPass.enabled = this.data.filmGrainEnabled;
        this.filmGrainPass.uniforms.amount.value = this.data.filmGrainAmount;
        this.filmGrainPass.uniforms.speed.value = this.data.filmGrainSpeed;
      }
      
      // Update chromatic aberration settings
      if (this.chromaticAberrationPass) {
        this.chromaticAberrationPass.enabled = this.data.chromaticAberrationEnabled;
        this.chromaticAberrationPass.uniforms.amount.value = this.data.chromaticAberrationAmount;
        this.chromaticAberrationPass.uniforms.radialFactor.value = this.data.chromaticAberrationRadialFactor;
      }
      
      // Update lens distortion settings
      if (this.lensDistortionPass) {
        this.lensDistortionPass.enabled = this.data.lensDistortionEnabled;
        this.lensDistortionPass.uniforms.strength.value = this.data.lensDistortionStrength;
        this.lensDistortionPass.uniforms.cylindricalRatio.value = this.data.lensDistortionCylindricalRatio;
      }
      
      // Update motion blur settings
      if (this.motionBlurPass) {
        this.motionBlurPass.enabled = this.data.motionBlurEnabled;
        this.motionBlurPass.uniforms.strength.value = this.data.motionBlurStrength;
      }
      
      // Update anamorphic flare settings
      if (this.anamorphicFlarePass) {
        this.anamorphicFlarePass.enabled = this.data.anamorphicFlareEnabled;
        this.anamorphicFlarePass.uniforms.intensity.value = this.data.anamorphicFlareIntensity;
        this.anamorphicFlarePass.uniforms.threshold.value = this.data.anamorphicFlareThreshold;
        this.anamorphicFlarePass.uniforms.color.value = new THREE.Vector3(
          parseInt(this.data.anamorphicFlareColor.slice(1, 3), 16) / 255,
          parseInt(this.data.anamorphicFlareColor.slice(3, 5), 16) / 255,
          parseInt(this.data.anamorphicFlareColor.slice(5, 7), 16) / 255
        );
        this.anamorphicFlarePass.uniforms.stretch.value = this.data.anamorphicFlareStretch;
      }
      
      // Update film damage settings
      if (this.filmDamagePass) {
        this.filmDamagePass.enabled = this.data.filmDamageEnabled;
        this.filmDamagePass.uniforms.scratches.value = this.data.filmDamageScratchAmount;
        this.filmDamagePass.uniforms.dust.value = this.data.filmDamageDustAmount;
        this.filmDamagePass.uniforms.dustSize.value = this.data.filmDamageDustSize;
        this.filmDamagePass.uniforms.scratchWidth.value = this.data.filmDamageScratchWidth;
      }
      
      // Update radial blur settings
      if (this.radialBlurPass) {
        this.radialBlurPass.enabled = this.data.radialBlurEnabled;
        this.radialBlurPass.uniforms.strength.value = this.data.radialBlurStrength;
        this.radialBlurPass.uniforms.centerX.value = this.data.radialBlurCenterX;
        this.radialBlurPass.uniforms.centerY.value = this.data.radialBlurCenterY;
      }
      
      // Update film emulation settings
      if (this.filmEmulationPass) {
        this.filmEmulationPass.enabled = this.data.filmEmulationEnabled;
        this.filmEmulationPass.uniforms.filmType.value = this.data.filmEmulationType;
        this.filmEmulationPass.uniforms.intensity.value = this.data.filmEmulationIntensity;
      }
      
      // Update cinemascope settings
      if (this.cinemascopePass) {
        this.cinemascopePass.enabled = this.data.cinemascopeEnabled;
        this.cinemascopePass.uniforms.aspectRatio.value = this.data.cinemascopeRatio;
        this.cinemascopePass.uniforms.opacity.value = this.data.cinemascopeOpacity;
      }
      
      // Update shader-based vignette settings
      if (this.vignettePass) {
        this.vignettePass.enabled = this.data.vignetteEnabled;
        this.vignettePass.uniforms.darkness.value = this.data.vignetteStrength;
        this.vignettePass.uniforms.vignetteColor.value = new THREE.Vector3(
          parseInt(this.data.vignetteColor.slice(1, 3), 16) / 255,
          parseInt(this.data.vignetteColor.slice(3, 5), 16) / 255,
          parseInt(this.data.vignetteColor.slice(5, 7), 16) / 255
        );
        this.vignettePass.uniforms.cornerWeight.value = this.data.vignetteCornerWeight;
      }
    },
  
    // Update effect visibility
    updateEffect: function(active) {
      // Update DOM elements (letterbox, vignette)
      const elements = [this.letterboxTop, this.letterboxBottom, this.vignette];
      const targetOpacity = active ? 1 : 0;
      
      elements.forEach(el => {
        if (el) {
          el.setAttribute('animation__opacity', {
            property: 'material.opacity',
            to: targetOpacity,
            dur: this.data.transitionDuration,
            easing: 'easeInOutQuad'
          });
        }
      });
      
      // Update shader passes
      const passes = [
        this.smaaPass,
        this.fxaaPass,
        this.bloomPass,
        this.colorGradingPass,
        this.enhancedDOFPass,
        this.sharpenPass,
        this.filmGrainPass,
        this.chromaticAberrationPass,
        this.lensDistortionPass,
        this.motionBlurPass,
        this.vignettePass,
        this.anamorphicFlarePass,
        this.filmDamagePass,
        this.radialBlurPass,
        this.filmEmulationPass,
        this.cinemascopePass
      ];
      
      passes.forEach(pass => {
        if (pass) {
          pass.enabled = active && pass.enabled;
        }
      });
    },
  
    update: function(oldData) {
      // Handle core settings changes
      if (this.data.active !== oldData.active) {
        this.updateEffect(this.data.active);
      }
      
      // Handle letterbox changes
      if (this.data.letterboxEnabled !== oldData.letterboxEnabled) {
        if (this.data.letterboxEnabled) {
          this.createLetterbox();
        } else if (this.letterboxTop && this.letterboxBottom) {
          this.el.removeChild(this.letterboxTop);
          this.el.removeChild(this.letterboxBottom);
          this.letterboxTop = null;
          this.letterboxBottom = null;
        }
      }
      if (this.data.letterboxHeight !== oldData.letterboxHeight && this.letterboxTop && this.letterboxBottom) {
        this.updateLetterbox();
      }
      
      // Handle vignette settings changes
      if (this.data.vignetteEnabled !== oldData.vignetteEnabled) {
        if (this.data.vignetteEnabled) {
          this.createVignette();
        } else if (this.vignette) {
          this.el.removeChild(this.vignette);
          this.vignette = null;
        }
      }
      if ((this.data.vignetteStrength !== oldData.vignetteStrength || 
           this.data.vignetteColor !== oldData.vignetteColor) && this.vignette) {
        this.updateVignette();
      }
      
      // Handle quality settings changes
      if (this.data.quality !== oldData.quality && this.data.quality !== 'auto') {
        this.applyQualityPreset(this.data.quality);
      }
      
      // Handle adaptive quality changes
      if (this.data.adaptiveQuality !== oldData.adaptiveQuality) {
        if (this.data.adaptiveQuality) {
          this.startPerformanceMonitoring();
        } else {
          this.stopPerformanceMonitoring();
        }
      }
      
      // Handle target FPS changes
      if (this.data.targetFPS !== oldData.targetFPS) {
        this.performanceMonitor.targetFrameTime = 1000 / this.data.targetFPS;
      }
      
      // Handle render scale changes
      if (this.data.renderScale !== oldData.renderScale) {
        console.log(`Render scale changed from ${oldData.renderScale} to ${this.data.renderScale}`);
        this.updateRenderScale();
      }
      
      // Check if we need to completely rebuild the post-processing pipeline
      if (this.needsRebuild(oldData)) {
        console.log("Major settings change detected - rebuilding post-processing pipeline");
        this.rebuildPostProcessing();
        return;
      }
      
      // Otherwise just update individual settings
      this.updatePostProcessing();
    },
  
    // Clean up when component is removed
    remove: function() {
      // Remove DOM elements
      if (this.letterboxTop) this.el.removeChild(this.letterboxTop);
      if (this.letterboxBottom) this.el.removeChild(this.letterboxBottom);
      if (this.vignette) this.el.removeChild(this.vignette);
      
      // Stop performance monitoring
      this.stopPerformanceMonitoring();
      
      // Remove window event listeners
      window.removeEventListener('resize', this.onResize);
  
      // Restore original renderer
      if (this.el.sceneEl.renderer) {
        this.el.sceneEl.renderer.render = this.originalRender;
      }
      
      // Dispose of render targets and composers
      if (this.composer) {
        this.composer.dispose();
      }
      if (this.dofComposer) {
        this.dofComposer.dispose();
      }
      if (this.dofRenderTarget) {
        this.dofRenderTarget.dispose();
      }
      if (this.prevFrameRT) {
        this.prevFrameRT.dispose();
      }
  
      // Remove event listeners
      if (this.renderStartListener) {
        this.el.sceneEl.removeEventListener('renderstart', this.renderStartListener);
      }
    }
  });
  
  // Export all camera components
  export const EnhancedCameraComponent = {
    cinematicCameraPro: AFRAME.components['cinematic-camera-pro']
  };